// Tenants (superusers only): create organizations, set purchased seats, switch into one.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router";
import { api, errorText } from "../api/client";
import { useUI } from "../app/context";
import { keys, useSession } from "../app/queries";

interface Tenant {
  id: number;
  name: string;
  seats: number;
  used: number;
}

export function Tenants() {
  const me = useSession();
  if (!me.superuser) return <Navigate to="/brief" replace />;
  return <TenantList />;
}

function TenantList() {
  const me = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useUI();
  const tenants = useQuery({ queryKey: ["tenants"], queryFn: () => api<{ tenants: Tenant[] }>("/api/tenants").then((d) => d.tenants) });
  const [name, setName] = useState("");
  const [seats, setSeats] = useState(5);
  const refresh = () => client.invalidateQueries({ queryKey: ["tenants"] });
  const create = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api("/api/tenants", { method: "POST", body: { name, seats } });
      setName("");
      setSeats(5);
      await Promise.all([refresh(), client.invalidateQueries({ queryKey: keys.me })]);
      showToast("Tenant created.");
    } catch (err) {
      showToast(errorText(err));
    }
  };
  const setTenantSeats = async (id: number, value: number) => {
    try {
      await api(`/api/tenants/${id}`, { method: "PATCH", body: { seats: value } });
      showToast("Seats updated.");
    } catch (err) {
      showToast(errorText(err));
    }
    await refresh();
  };
  const switchTo = async (id: number) => {
    await api("/api/tenant", { method: "POST", body: { tenant_id: id } });
    await client.resetQueries();
    navigate("/brief");
  };
  return (
    <div className="admin">
      <div className="top">
        <div className="date">Operator</div>
      </div>
      <h1>Tenants</h1>
      <form className="inline" id="add-tenant" style={{ gridTemplateColumns: "minmax(0,1fr) 130px auto" }} onSubmit={create}>
        <label>
          <span>Organization</span>
          <input id="at-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          <span>Seats</span>
          <input type="number" id="at-seats" min={1} required value={seats} onChange={(e) => setSeats(Number(e.target.value))} />
        </label>
        <button className="btn primary">Create tenant</button>
      </form>
      {tenants.data ? (
        <table>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Seats used</th>
              <th>Seats</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tenants.data.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td>{t.used}</td>
                <td>
                  <SeatsInput tenant={t} onChange={(v) => setTenantSeats(t.id, v)} />
                </td>
                <td className="actions">
                  <button className="btn" data-switch={t.id} disabled={t.id === me.tenant?.id} onClick={() => switchTo(t.id)}>
                    {t.id === me.tenant?.id ? "Current" : "Switch to"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="empty">Loading…</p>
      )}
    </div>
  );
}

/** Saves on change (blur or arrows), like the old page; typing alone doesn't save each keystroke. */
function SeatsInput({ tenant, onChange }: { tenant: Tenant; onChange: (value: number) => void }) {
  const [value, setValue] = useState(String(tenant.seats));
  const commit = () => {
    const n = Number(value);
    if (n >= 1 && n !== tenant.seats) onChange(n);
    else setValue(String(tenant.seats));
  };
  return (
    <input
      type="number"
      min={1}
      value={value}
      data-seats={tenant.id}
      aria-label={`Seats for ${tenant.name}`}
      style={{ width: 90 }}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
      }}
    />
  );
}
